import { useState, useEffect } from 'react';
import type { RefObject } from 'react';
import { authService } from './authService';
import type { ListJsSort } from './types';

/**
 * Drop-in replacement for useState that persists sort state to localStorage,
 * namespaced per user. Works with any value shape (string, object, null, etc.)
 *
 * Usage:
 *   const [sortConfig, setSortConfig] = useSortPreference('project-vehicles', { key: 'name', direction: 'asc' });
 *   const [sortField, setSortField] = useSortPreference('rentals-field', 'date_from');
 */
export function useSortPreference<T>(tableKey: string, defaultValue: T): [T, (val: T) => void] {
  const userId = authService.getUser()?.id ?? 'guest';
  const storageKey = `sort_${userId}_${tableKey}`;

  const [value, setValue] = useState<T>(() => {
    try {
      const raw = localStorage.getItem(storageKey);
      if (raw !== null) return JSON.parse(raw) as T;
    } catch {
      // ignore corrupt storage — fall through to default
    }
    return defaultValue;
  });

  useEffect(() => {
    localStorage.setItem(storageKey, JSON.stringify(value));
  }, [storageKey, value]);

  return [value, setValue];
}

// ─── List.js helpers ─────────────────────────────────────────────────────────
// Use these in tables driven by List.js (e.g. Transport, Crew, Customers).

function getStorageKey(tableKey: string): string {
  const userId = authService.getUser()?.id ?? 'guest';
  return `sort_${userId}_${tableKey}`;
}

/** Read persisted { col, order } for a List.js table. Returns null if nothing saved. */
export function readListJsSort(tableKey: string): ListJsSort | null {
  try {
    return JSON.parse(localStorage.getItem(getStorageKey(tableKey)) ?? 'null') as ListJsSort | null;
  } catch {
    return null;
  }
}

/** Persist { col, order } for a List.js table. */
export function saveListJsSort(tableKey: string, col: string, order: 'asc' | 'desc'): void {
  localStorage.setItem(getStorageKey(tableKey), JSON.stringify({ col, order }));
}

/**
 * Attach a click listener to a table container that auto-persists the active
 * List.js sort state after each sort click.
 *
 * Returns a cleanup function — call it in your useEffect return.
 *
 * @param tableKey   - unique key for this table
 * @param containerRef - ref pointing to the table wrapper
 */
export function attachListJsSortPersistence(
  tableKey: string,
  containerRef: RefObject<HTMLElement>,
): () => void {
  const el = containerRef?.current;
  if (!el) return () => {};

  const handler = (e: Event): void => {
    const target = e.target as HTMLElement;
    if (!target.closest('[data-sort]')) return;
    setTimeout(() => {
      const active = el.querySelector('.table-sort.asc, .table-sort.desc') as HTMLElement | null;
      if (active) {
        saveListJsSort(
          tableKey,
          active.getAttribute('data-sort') ?? '',
          active.classList.contains('asc') ? 'asc' : 'desc',
        );
      }
    }, 0);
  };

  el.addEventListener('click', handler);
  return () => el.removeEventListener('click', handler);
}
